import React, { useState } from "react";
import CardImg from "../../../components/CardImg";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import { getCardImage } from "../../casinoComponents/resultVisuals/cardAssets";
import ResultTeenMuf from "../resultui/resultTeenMuf/ResultTeenMuf";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import TeenMufRightSide from "./TeenMufRightSide";

/**
 * Virtual Muflis Teenpatti (vcasino/teenmuf, API type vteenmuf) in the reference markup:
 *   .casino-table.vcasino.vmuflis >
 *     [.progress.mobile-timer (ft > 3)]
 *     .casino-video > .casino-video-title + .video-box-container + .vcasino-cards + right icons (home, rules) + rules
 *     .casino-detail > .main-odds (.odds-title + Winner / TOP 9 / M Baccarat .odds-section rows of 3 .vodds-box)
 *                    + [remark] + .casino-video-last-results
 *   right sidebar: My Bets (UserName / Rate / Amount) + Rules card (Top 9 payouts).
 */

const WINNER_LEFT = `${import.meta.env.BASE_URL}img/winner-left.png`;
const REMARK_ICON = `${import.meta.env.BASE_URL}assets/img/icons/remark.png`;
const EMPTY = { b: 0, min: 0, max: 0, gstatus: "SUSPENDED" };
const RANGE_INDENT = "\n                                            Range: ";

// Reference v-kvalmin / v-kvalmax: 25 → "25", 25000 → "25K", 100000 → "1L"
function kval(num) {
  const n = Number(num) || 0;
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(1))}K`;
  return String(n);
}

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

export default function VTeenMufView({
  gameName = "vteenmuf",
  title = "V-Muflis Teenpatti",
  data,
  exposures = {},
  onBetClick,
  myBets,
  onViewMore,
  viewMoreModal,
}) {
  // Bootstrap collapse in the reference: each range popup toggles independently.
  // undefined = never toggled (no aria-expanded / .collapsed on the icon yet)
  const [ranges, setRanges] = useState({});
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const rulesHtml = getCasinoRulesHtml(gameName) || getCasinoRulesHtml("teenmuf") || "";

  const sub = Array.isArray(data?.sub) ? data.sub : [];
  const ofType = (t) => sub.filter((s) => s?.subtype === t).sort(bySr);
  const muflis = ofType("vteenmuf");
  const top9 = ofType("vtop9");
  const baccarat = ofType("vmbacc");
  const at = (arr, i) => arr[i] || EMPTY;
  const cards = data?.card ? String(data.card).split(",").map((c) => c.trim()) : [];
  const ft = Number(data?.ft) || 0;
  const lt = Number(data?.lt) || 0;
  const rdesc = String(data?.rdesc ?? "");
  const remark = data?.remark;

  const exposureOf = (nat) => {
    const v = exposures?.[nat] ?? exposures?.[String(nat || "").toLowerCase()];
    return Number(v) || 0;
  };

  const suspended = (item) => String(item?.gstatus || "").toUpperCase() !== "OPEN";

  const oddBox = (item) => {
    const v = exposureOf(item.nat);
    return (
      <div className="vodds-box">
        <div className={`casino-bl-box${suspended(item) ? " suspended" : ""}`}>
          <div
            className="back casino-bl-box-item"
            onClick={() => !suspended(item) && onBetClick?.(item.b, item.nat, "back", item)}
          >
            <span className="casino-box-odd">{item.b}</span>{" "}
            <span className={v > 0 ? "book-green" : "book-red"}>{formatBook(v)}</span>
          </div>
        </div>
      </div>
    );
  };

  const toggle = (id) => setRanges((p) => ({ ...p, [id]: !p[id] }));

  const section = (label, n, items) => {
    const id = `demo${n}`;
    const a = at(items, 0);
    const b = at(items, 1);
    const state = ranges[id];
    return (
      <div className="odds-section">
        {oddBox(a)}{" "}
        <div className="vodds-box">
          <b>{label}</b>{" "}
          <div className="d-inline-block ml-2">
            <i
              role="button"
              tabIndex={0}
              data-toggle="collapse"
              data-target={`#${id}`}
              aria-expanded={state === undefined ? undefined : String(state)}
              className={`fas fa-info-circle${state === false ? " collapsed" : ""}`}
              onClick={() => toggle(id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  toggle(id);
                }
              }}
            />{" "}
            <Collapse id={id} open={!!state} className="icon-range">
              {RANGE_INDENT}
              <span>{kval(a.min)}</span>-<span>{kval(a.max)}</span>
            </Collapse>
          </div>
        </div>{" "}
        {oddBox(b)}
      </div>
    );
  };

  const card = (token) => (
    <span>
      <CardImg src={getCardImage(token)} alt="" />
    </span>
  );

  return (
    <CasinoTableLayout
      tableClass="vcasino vmuflis"
      rightSide={<TeenMufRightSide virtual bets={myBets} onViewMore={onViewMore} />}
    >
      <>
        {ft > 3 && (
          <div className="progress mobile-timer">
            <div
              className={`progress-bar ${lt <= 3 ? "bg-danger" : lt <= 6 ? "bg-warning" : "bg-success"}`}
              style={{ width: `${((100 * lt) / ft).toFixed(2)}%` }}
            />
          </div>
        )}{" "}
        <div className="casino-video">
          <div className="casino-video-title">
            <span className="casino-name">{title}</span>{" "}
            <div className="casino-video-rid">Round ID: {data?.mid}</div>
          </div>{" "}
          <div className="video-box-container">
            <div className="video-box" />
          </div>{" "}
          {cards.length > 0 && (
            <div className="vcasino-cards">
              <div className="player-card playera-card">
                {rdesc === "1" && <img src={WINNER_LEFT} className="v-winner" alt="" />}{" "}
                <div className="w-100 playera mb-2">Player A</div> {card(cards[0])} {card(cards[2])} {card(cards[4])}
              </div>{" "}
              <div className="player-card playerb-card">
                {rdesc === "2" && <img src={WINNER_LEFT} className="v-winner" alt="" />}{" "}
                <div className="w-100 playerb mb-2">Player B</div> {card(cards[5])} {card(cards[3])} {card(cards[1])}
              </div>
            </div>
          )}{" "}
          <div className="casino-video-right-icons">
            <div title="Home" className="casino-video-home-icon">
              <Link to="/admin/vcasino/list" style={{ color: "var(--text-highlight)" }}>
                <i className="fas fa-home" />
              </Link>
            </div>{" "}
            <div
              title="Rules"
              className="casino-video-rules-icon"
              onClick={() => {
                setShowRules(true);
                if (IS_REF_MOBILE) setShowRulesModal(true);
              }}
            >
              <i className="fas fa-info-circle" />
            </div>
          </div>{" "}
          <div>
            {showRules && (
              <div
                id="casino-vieo-rules"
                aria-modal="true"
                className="casino-vieo-rules show d-none-small show-rules"
                style={{ display: "block" }}
              >
                <div className="rules-header">
                  <div>Rules</div> <i className="fas fa-times" onClick={() => setShowRules(false)} />
                </div>{" "}
                <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
              </div>
            )}
            <Modal
              show={showRulesModal}
              onHide={() => setShowRulesModal(false)}
              className="casino-rules teenpatti2-rule-modal"
              dialogClassName="modal-md"
              id="rules-modal"
            >
              <header className="modal-header">
                <h5 className="modal-title">Rules</h5>{" "}
                <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
                  ×
                </button>
              </header>
              <Modal.Body>
                <div className="casino-vieo-rules show-rules show">
                  <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </div>{" "}
        {data?.mid ? (
          <div className="casino-detail">
            <div className="main-odds">
              <div className="odds-title">
                <div className="playera">Player A</div> <div>{" "}</div> <div className="playerb">Player B</div>
              </div>{" "}
              {section("Winner", 1, muflis)}{" "}
              {section("TOP 9", 2, top9)}{" "}
              {section("M Baccarat", 3, baccarat)}
            </div>{" "}
            {remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={REMARK_ICON} alt="" />
                </div>{" "}
                <marquee>{remark}</marquee>
              </div>
            ) : null}{" "}
            <div className="casino-video-last-results">
              <ResultTeenMuf gameType={gameName} fallbackTitle={title} />
            </div>
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
